import {useState} from 'react'
import {useParams} from 'react-router'
import {
  useWt,
  useWuShowToast,
  WuButton,
  WuIcon,
  WuLoader,
  WuTextarea,
  WuTooltip,
} from '@npm-questionpro/wick-ui-lib'
import type {IResponseStat} from '../../../../types'
import {cx} from '../../../../utils/classNameUtil'
import {dateUtil} from '../../../../utils/dateUtil'
import {DIALOG} from '../../../../Translation/English/dialog'
import {GLOBAL} from '../../../../Translation/English/global'
import {dialogApi} from '../../../../api/dialogApi'
import {BaseModal} from '../../../../components/BaseModal/BaseModal'
import {ConfirmModalContent} from '../../../../components/ConfirmModalContent'
import {DialogAvatar} from '../DialogAvatar/DialogAvatar'
import {ModerationGuidelines} from '../ModerationGuidelines/ModerationGuidelines'
import styles from './DialogTextCard.module.scss'

interface IDialogTextCardProps {
  data: IResponseStat
  isUserModerator?: boolean
  isReply?: boolean
  onDeleteRequest?: () => void
}

export const DialogTextCard: React.FC<IDialogTextCardProps> = ({
  data,
  isUserModerator = false,
  isReply = false,
  onDeleteRequest,
}) => {
  const updateDialogResponseMutation = dialogApi.useUpdateDialogResponse()
  const deleteDialogResponseReplyMutation =
    dialogApi.useDeleteDialogResponseReply()
  const wt = useWt()
  const {showToast} = useWuShowToast()
  const {dialogId} = useParams<{dialogId: string}>()

  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [responseText, setResponseText] = useState<string>(data.text)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [isShowDeleteReplyModal, setIsShowDeleteReplyModal] =
    useState<boolean>(false)
  const [deleteReplyModalFooter, setDeleteReplyModalFooter] =
    useState<React.ReactNode>(null)

  const isHighLighted = (response: IResponseStat): string | undefined => {
    return response.isHighlighted ? styles.starBlue : undefined
  }

  const toggleHighLightResponse = async (
    response: IResponseStat,
  ): Promise<void> => {
    try {
      await updateDialogResponseMutation.mutateAsync({
        id: String(response.id),
        isHighlighted: !response.isHighlighted,
        dialogId,
      })
    } catch {
      showToast({message: wt(DIALOG.failedToUpdateHighlight), variant: 'error'})
    }
  }

  const startEdit = (): void => {
    setResponseText(data.text)
    setIsEditing(true)
  }

  const cancelEdit = (): void => {
    setResponseText(data.text)
    setIsEditing(false)
  }

  const saveEdit = async (): Promise<void> => {
    try {
      await updateDialogResponseMutation.mutateAsync({
        id: String(data.id),
        text: responseText,
        dialogId,
      })
      showToast({message: wt(DIALOG.responseUpdated), variant: 'success'})
      setIsEditing(false)
    } catch {
      showToast({message: wt(DIALOG.failedToUpdateResponse), variant: 'error'})
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setResponseText(e.target.value)
  }

  const confirmDeleteReply = async (): Promise<void> => {
    try {
      await deleteDialogResponseReplyMutation.mutateAsync({
        id: String(data.id),
        dialogId,
      })
      showToast({message: wt(DIALOG.replyDeleted), variant: 'success'})
      setIsShowDeleteReplyModal(false)
    } catch {
      showToast({message: wt(DIALOG.failedToDeleteReply), variant: 'error'})
    }
  }

  return (
    <div className={styles.dialogTextCard} key={`dialog-item--${data.id}`}>
      <div className={styles.wrapper}>
        <div className={styles.userDetails}>
          <DialogAvatar
            displayName={
              data.isModerator
                ? wt(DIALOG.moderator)
                : (data.dialogUserAvtar?.username ?? '')
            }
            avatarUsername={
              data.isModerator ? undefined : data.dialogUserAvtar?.username
            }
          />
          <div className={styles.username}>
            {data.isModerator
              ? wt(DIALOG.moderator)
              : data.dialogUserAvtar?.username}
          </div>
          <div className={styles.timestamp}>
            {dateUtil.formatDate(data.createdAt, 'mediumWithSeconds')}
          </div>
        </div>
        <div className={styles.headerActions}>
          {!isReply && isUserModerator && (
            <WuTooltip Content={`${wt(GLOBAL.edit)} ${wt(DIALOG.response)}`}>
              <WuButton
                size="sm"
                iconOnly
                variant="secondary"
                Icon={<WuIcon icon="wm-edit" />}
                aria-label={`${wt(GLOBAL.edit)} ${wt(DIALOG.response)}`}
                onClick={startEdit}
              />
            </WuTooltip>
          )}
          {!isReply && onDeleteRequest && isUserModerator && (
            <WuTooltip Content={`${wt(GLOBAL.delete)} ${wt(DIALOG.response)}`}>
              <WuButton
                size="sm"
                iconOnly
                variant="secondary"
                Icon={<WuIcon icon="wm-delete" />}
                aria-label={`${wt(GLOBAL.delete)} ${wt(DIALOG.response)}`}
                onClick={onDeleteRequest}
              />
            </WuTooltip>
          )}
          {isReply && isUserModerator && (
            <WuTooltip Content={`${wt(GLOBAL.delete)} ${wt(DIALOG.reply)}`}>
              <WuButton
                size="sm"
                iconOnly
                variant="secondary"
                Icon={<WuIcon icon="wm-delete" />}
                aria-label={`${wt(GLOBAL.delete)} ${wt(DIALOG.reply)}`}
                onClick={() => setIsShowDeleteReplyModal(true)}
              />
            </WuTooltip>
          )}
          {isUserModerator && !isReply && (
            <WuTooltip Content={wt(DIALOG.highlightResponse)}>
              <WuButton
                size="sm"
                iconOnly
                variant="secondary"
                aria-label={wt(DIALOG.highlightResponse)}
                Icon={
                  <WuIcon
                    icon="wm-star"
                    className={cx(styles.star, isHighLighted(data))}
                  />
                }
                onClick={() => toggleHighLightResponse(data)}
              />
            </WuTooltip>
          )}
        </div>
      </div>
      {!isReply && isEditing && (
        <div className={styles.editBlock}>
          <div className={styles.editInputWrapper}>
            {updateDialogResponseMutation.isPending && (
              <div className={styles.loadingOverlay}>
                <WuLoader size="sm" />
              </div>
            )}
            <WuTextarea
              className={styles.editInput}
              value={responseText}
              onChange={handleChange}
              disabled={updateDialogResponseMutation.isPending}
            />
            <div className={styles.editActions}>
              <button
                type="button"
                className={styles.btnCancel}
                onClick={cancelEdit}
                disabled={updateDialogResponseMutation.isPending}
              >
                {wt(GLOBAL.cancel)}
              </button>
              <WuButton
                size="sm"
                disabled={
                  updateDialogResponseMutation.isPending ||
                  responseText.trim().length < 1 ||
                  responseText === data.text
                }
                onClick={() => saveEdit()}
              >
                {wt(GLOBAL.save)}
              </WuButton>
            </div>
          </div>
        </div>
      )}
      {!isEditing && (!data.isFlagged || data.status == 'Approved') && (
        <div className={cx(styles.text, isReply && styles.textReply)}>
          {data.text}
        </div>
      )}
      {data.isFlagged && isReply && data.status == 'Pending' && (
        <div className={styles.moderation}>
          <div className={styles.moderationWarning}>
            <WuIcon
              icon="wm-error-circle-rounded"
              className={styles.moderationIcon}
            />
            {wt(DIALOG.replyWarning)}
          </div>
          <div className={styles.moderationText}>
            {wt(DIALOG.replyWarningDescription)}
            <button
              type="button"
              className={styles.moderationLink}
              onClick={() => setShowModal(true)}
            >
              {wt(DIALOG.communityGuidelines)}
            </button>
          </div>
          <div className={styles.moderationTimestamp}>
            {dateUtil.formatDate(data.createdAt, 'mediumWithSeconds')}
          </div>
          {showModal && (
            <ModerationGuidelines
              showModal={showModal}
              setShowModal={setShowModal}
            />
          )}
        </div>
      )}
      <BaseModal
        visible={isShowDeleteReplyModal}
        headerTitle={`${wt(GLOBAL.delete)} ${wt(DIALOG.reply)}`}
        onCloseModal={() => setIsShowDeleteReplyModal(false)}
        isLoading={deleteDialogResponseReplyMutation.isPending}
        maxWidth="600px"
        variant="critical"
        footer={deleteReplyModalFooter}
      >
        <ConfirmModalContent
          isLoading={deleteDialogResponseReplyMutation.isPending}
          description={`${wt(GLOBAL.deleteItemText)} ${wt(DIALOG.reply)}?`}
          handleConfirm={confirmDeleteReply}
          handleCancel={() => setIsShowDeleteReplyModal(false)}
          setContent={setDeleteReplyModalFooter}
          isDestructive
        />
      </BaseModal>
    </div>
  )
}
